import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableRow, TableHead, TableSortLabel,
  Paper, Typography, TextField, MenuItem, Box, Grid, Card, CardContent, Alert,
  IconButton, Tooltip
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { BarChart } from '@mui/x-charts/BarChart';
import { getPositionsV2 } from '../../api/wallet';
import { formatValue, getAssetClasses, getAssetInstrumentTypesByClass, getInstrumentTypeLabel } from '../../utils/utils';
import useSorting from '../../utils/useSorting';
import { CHART_COLORS } from '../../constants/layout';
import IncomeHistoryModal from './IncomeHistoryModal';

const assetClassOptions = [
  { value: '', label: 'Todos' },
  ...getAssetClasses(),
];

const PositionsTable = ({ currencyView }) => {
  const [positions, setPositions] = useState([]);
  const [totalPositions, setTotalPositions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    displayCurrency: 'BRL',
    assetClass: '',
    assetName: '',
    instrumentType: '',
  });
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [incomeHistoryOpen, setIncomeHistoryOpen] = useState(false);

  const handleOpenIncomeHistory = (position) => {
    setSelectedPosition(position);
    setIncomeHistoryOpen(true);
  };

  const handleCloseIncomeHistory = () => {
    setIncomeHistoryOpen(false);
    setSelectedPosition(null);
  };

  const columnGroups = [
    { label: 'Ativo', colSpan: 2, color: '#546e7a' },
    { label: 'Posicao', colSpan: 2, color: '#607d8b' },
    { label: 'Investimento', colSpan: 2, color: '#78909c' },
    { label: 'Performance', colSpan: 3, color: '#546e7a' },
    { label: 'Proventos', colSpan: 2, color: '#607d8b' },
  ];

  const columns = [
    { id: 'Asset.InstrumentType', label: 'Tipo', sortable: true },
    { id: 'Asset.Name', label: 'Nome', sortable: true },
    { id: 'Quantity', label: 'Qtd', sortable: true },
    { id: 'Percentage', label: '% Carteira', sortable: true },
    { id: 'AveragePrice', label: 'PM', sortable: true },
    { id: 'TotalInvested', label: 'Total', sortable: true },
    { id: 'CurrentValue', label: 'Atual', sortable: true },
    { id: 'ProfitLoss', label: 'L/P', sortable: true },
    { id: 'ProfitLossPercentage', label: '%', sortable: true },
    { id: 'TotalIncome', label: 'Total', sortable: true },
    { id: 'CumulativeYieldRate', label: 'Yield %', sortable: true },
  ];

  const createSortHandler = (property) => (event) => {
    handleRequestSort(event, property);
  };

  const valueTransformers = {
    Percentage: (position) => parseFloat(position.percentage) || 0,
    TotalInvested: (position) => parseFloat(position.total_invested) || 0,
    CurrentValue: (position) => parseFloat(position.current_value) || 0,
    ProfitLoss: (position) => parseFloat(position.profit_loss) || 0,
    ProfitLossPercentage: (position) => parseFloat(position.profit_loss_percentage) || 0,
    TotalIncome: (position) => parseFloat(position.total_income) || 0,
    CumulativeYieldRate: (position) => parseFloat(position.cumulative_yield_rate) || 0,
  };

  const { orderBy, order, sortedData, handleRequestSort } = useSorting(positions, 'Asset.Name', 'asc', valueTransformers);

  const fetchPositions = async (overrideFilters) => {
    const f = overrideFilters || filters;
    try {
      const response = await getPositionsV2(currencyView, {
        AssetClass: f.assetClass || undefined,
        AssetName: f.assetName || undefined,
        InstrumentType: f.instrumentType || undefined,
      });
      setPositions(Array.isArray(response.data.positions) ? response.data.positions : []);
      setTotalPositions(response.data.total || {});
      setError(null);
    } catch (err) {
      setError('Erro ao buscar posicoes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPositions();
  }, [currencyView]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));

    if (name === 'assetClass') {
      setLoading(true);
      const newFilters = { ...filters, assetClass: value, instrumentType: '' };
      setFilters(newFilters);
      setTimeout(() => {
        fetchPositions(newFilters);
      }, 0);
    }

    if (name === 'instrumentType') {
      setLoading(true);
      const newFilters = { ...filters, instrumentType: value };
      setFilters(newFilters);
      setTimeout(() => {
        fetchPositions(newFilters);
      }, 0);
    }

    if (name === 'assetName') {
      if (debounceTimeout) clearTimeout(debounceTimeout);
      const newFilters = { ...filters, assetName: value };
      setFilters(newFilters);
      const timeout = setTimeout(() => {
        setLoading(true);
        fetchPositions(newFilters);
      }, 400);
      setDebounceTimeout(timeout);
    }
  };

  const calculateMargin = (dataset) => {
    if (!dataset.length) {
      return { left: 40, right: 10, top: 40, bottom: 30 };
    }
    const maxLabelLength = Math.max(...dataset.map((data) => data.name.length));
    return {
      left: maxLabelLength * 12,
      right: 10,
      top: 40,
      bottom: 30,
    };
  };

  const renderBarChart = () => {
    const dataset = sortedData.map((p) => ({
      name: p.asset_name,
      investido: parseFloat(p.total_invested || 0),
      atual: parseFloat(p.current_value || 0),
    }));

    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Investimento vs Valor Atual ({currencyView})
        </Typography>
        <BarChart
          dataset={dataset}
          xAxis={[{ scaleType: 'band', dataKey: 'name' }]}
          series={[
            { dataKey: 'investido', label: 'Investido', color: CHART_COLORS[0] },
            { dataKey: 'atual', label: 'Atual', color: CHART_COLORS[1] },
          ]}
          height={350}
          margin={calculateMargin(dataset)}
          grid={{ horizontal: true }}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filtros
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select label="Classe do Ativo" name="assetClass" size="small"
                value={filters.assetClass} onChange={handleFilterChange} fullWidth
              >
                {assetClassOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select label="Instrumento" name="instrumentType" size="small"
                value={filters.instrumentType} onChange={handleFilterChange} fullWidth
                disabled={!filters.assetClass}
              >
                <MenuItem value="">Todos</MenuItem>
                {getAssetInstrumentTypesByClass(filters.assetClass).map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Nome do Ativo" name="assetName" size="small"
                value={filters.assetName} onChange={handleFilterChange} fullWidth
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {!loading && !error && positions.length > 0 && renderBarChart()}

      {loading ? (
        <Typography variant="h6">Carregando...</Typography>
      ) : error ? (
        <Typography variant="h6" color="error">{error}</Typography>
      ) : positions.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          Nenhuma posicao encontrada. Cadastre uma nova operacao para acompanhar seus investimentos.
        </Alert>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columnGroups.map((group, idx) => (
                  <TableCell
                    key={idx}
                    colSpan={group.colSpan}
                    align="center"
                    sx={{
                      backgroundColor: group.color,
                      color: 'white',
                      fontWeight: 'bold',
                      borderRight: idx < columnGroups.length - 1 ? '2px solid white' : 'none',
                      py: 0.5,
                    }}
                  >
                    {group.label}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align="left"
                    sortDirection={orderBy === column.id ? order : false}
                    sx={{ py: 0.5, whiteSpace: 'nowrap' }}
                  >
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={createSortHandler(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((position, index) => {
                const percentage = position.percentage || 'N/A';
                const averagePrice = formatValue(position.average_price, currencyView) || 'N/A';
                const totalInvested = formatValue(position.total_invested, currencyView) || 'N/A';
                const currentValue = formatValue(position.current_value, currencyView) || 'N/A';
                const profitLoss = formatValue(position.profit_loss, currencyView) || 'N/A';
                const profitLossPercentage = position.profit_loss_percentage || 'N/A';
                const instrumentType = position.instrument_type;
                const totalIncome = position.total_income ? formatValue(position.total_income, currencyView) : '-';
                const yieldRate = position.cumulative_yield_rate ? parseFloat(position.cumulative_yield_rate).toFixed(2) : '-';

                const rawProfitLoss = parseFloat(position.profit_loss) || 0;
                const rawProfitLossPercentage = parseFloat(profitLossPercentage) || 0;
                const rawYieldRate = parseFloat(position.cumulative_yield_rate) || 0;

                return (
                  <TableRow key={index} hover>
                    <TableCell>{instrumentType ? getInstrumentTypeLabel(instrumentType) : '-'}</TableCell>
                    <TableCell>{position.asset_name}</TableCell>
                    <TableCell>{position.quantity}</TableCell>
                    <TableCell>{percentage}%</TableCell>
                    <TableCell>{averagePrice}</TableCell>
                    <TableCell>{totalInvested}</TableCell>
                    <TableCell>{currentValue}</TableCell>
                    <TableCell sx={{ color: rawProfitLoss < 0 ? 'red' : 'green' }}>
                      {profitLoss}
                    </TableCell>
                    <TableCell sx={{ color: rawProfitLossPercentage < 0 ? 'red' : 'green' }}>
                      {profitLossPercentage}%
                    </TableCell>
                    <TableCell sx={{ color: rawYieldRate > 0 ? 'green' : 'inherit' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {totalIncome}
                        {position.income_count > 0 && !position.is_total && (
                          <Tooltip title="Ver historico de rendimentos">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenIncomeHistory(position)}
                              sx={{ p: 0.25 }}
                            >
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: rawYieldRate > 0 ? 'green' : 'inherit' }}>
                      {yieldRate !== '-' ? `${yieldRate}%` : '-'}
                    </TableCell>
                  </TableRow>
                );
              })}

              {positions.length > 0 && (
                <TableRow sx={{ backgroundColor: (theme) => theme.palette.action.hover }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>Total</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>-</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>100%</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>-</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{formatValue(totalPositions.total_invested, currencyView) || 'N/A'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{formatValue(totalPositions.current_value, currencyView) || 'N/A'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: parseFloat(totalPositions.profit_loss || 0) < 0 ? 'red' : 'green' }}>
                    {formatValue(totalPositions.profit_loss, currencyView) || 'N/A'}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: parseFloat(totalPositions.profit_loss_percentage) < 0 ? 'red' : 'green' }}>
                    {totalPositions.profit_loss_percentage || 'N/A'}%
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: parseFloat(totalPositions.total_income || 0) > 0 ? 'green' : 'inherit' }}>
                    {totalPositions.total_income ? formatValue(totalPositions.total_income, currencyView) : '-'}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>-</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <IncomeHistoryModal
        open={incomeHistoryOpen}
        onClose={handleCloseIncomeHistory}
        position={selectedPosition}
        currencyView={currencyView}
      />
    </Box>
  );
};

export default PositionsTable;
